import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';

import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import Header from './components/Header';
import ProtectedRoute from './components/ProtectedRoute';
import RedirectIfAuthenticated from './components/RedirectIfAuthenticated';

// Critical path — loaded eagerly
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import PromptsPage from './pages/PromptsPage';
import PromptDetail from './pages/PromptDetail';
import Footer from './components/Footer';
import CookieConsentBanner from './components/CookieConsentBanner';

// Heavy pages — code-split for faster initial bundle
const CreatePrompt = lazy(() => import('./pages/CreatePrompt'));
const MyPrompts = lazy(() => import('./pages/MyPrompts'));
const AdminPanel = lazy(() => import('./pages/AdminPanelEnhanced'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const Settings = lazy(() => import('./pages/Settings'));
const Favorites = lazy(() => import('./pages/Favorites'));
const Forums = lazy(() => import('./pages/Forums'));
const AIToolsPage = lazy(() => import('./pages/AIToolsPage'));
const EditPrompt = lazy(() => import('./pages/EditPrompt'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const CreateBlogPost = lazy(() => import('./pages/CreateBlogPost'));
const FeaturedProjectsPage = lazy(() => import('./pages/FeaturedProjectsPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const NotFound = lazy(() => import('./pages/NotFound'));

const PageLoader: React.FC = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      color: 'var(--surface-1)',
      fontSize: 13,
      fontFamily: 'Inter, sans-serif',
    }}
  >
    Loading...
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <ThemeProvider>
          <AuthProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <div className="min-h-screen" style={{ background: 'var(--base)', color: 'var(--stone)' }}>
              <Header />
              <main id="main-content">
            <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/prompts" element={<PromptsPage />} />
              <Route path="/projects" element={<Navigate to="/" replace />} />
<Route path="/communities" element={<Navigate to="/forums" replace />} />
              <Route path="/login" element={<RedirectIfAuthenticated><Login /></RedirectIfAuthenticated>} />
              <Route path="/register" element={<RedirectIfAuthenticated><Register /></RedirectIfAuthenticated>} />
              <Route path="/create" element={<ProtectedRoute><CreatePrompt /></ProtectedRoute>} />
              <Route path="/edit/:id" element={
                <ProtectedRoute requiredRole="admin">
                  <EditPrompt />
                </ProtectedRoute>
              } />
              <Route path="/my-prompts" element={<ProtectedRoute><MyPrompts /></ProtectedRoute>} />
              <Route path="/admin" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminPanel />
                </ProtectedRoute>
              } />
              <Route path="/profile/:username" element={<UserProfile />} />
              <Route path="/prompts/:id" element={<PromptDetail />} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
              <Route path="/forums" element={<Forums />} />
              <Route path="/tools" element={<AIToolsPage />} />
              <Route path="/featured-projects" element={<FeaturedProjectsPage />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/new" element={<ProtectedRoute requiredRole="admin"><CreateBlogPost /></ProtectedRoute>} />
              <Route path="/blog/:id" element={<BlogPost />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </main>
          <Footer />
          <CookieConsentBanner />
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#1C1C20',
                color: '#F2EDE4',
                borderRadius: '6px',
                border: '1px solid rgba(255,255,255,0.10)',
                fontSize: '13px',
                fontWeight: '500',
                padding: '10px 16px',
                boxShadow: '0 4px 24px rgba(0,0,0,0.6)',
                fontFamily: 'Inter, sans-serif',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#E8B84B',
                  secondary: '#0C0C0E',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#F87171',
                  secondary: '#0C0C0E',
                },
              },
            }}
          />
            </div>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
    </HelmetProvider>
    );
  }

export default App;
